import { useState } from "react";
import { Building2, Hotel, Utensils, Briefcase, Sparkles, TrendingDown, Clock, Users, Send, CheckCircle } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { toast } from "sonner";

const CLIENTS = [
  { icon: Hotel, label: "Hotels & Resorts" },
  { icon: Utensils, label: "Restaurants & Cafes" },
  { icon: Building2, label: "Hospitals & Clinics" },
  { icon: Briefcase, label: "Corporate Offices" },
  { icon: Users, label: "PGs & Hostels" },
  { icon: Sparkles, label: "Salons & Spas" },
];

const BENEFITS = [
  { icon: TrendingDown, title: "Volume-based pricing", desc: "Custom rate cards for bulk orders — save up to 40% vs retail." },
  { icon: Clock, title: "Daily & weekly pickups", desc: "Scheduled routes to your property. No follow-ups needed." },
  { icon: Users, title: "Dedicated account manager", desc: "Single point of contact for all your laundry needs and escalations." },
  { icon: CheckCircle, title: "SLA-backed turnaround", desc: "Fixed turnaround times with penalty clauses. Written commitments." },
];

export default function CorporatePage() {
  const [form, setForm] = useState({ business: "", contact: "", phone: "", email: "", volume: "", notes: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Submit via WhatsApp for now (no backend endpoint yet)
    const message = `Hi Blitzwash, corporate enquiry:\nBusiness: ${form.business}\nContact: ${form.contact}\nPhone: ${form.phone}\nEmail: ${form.email}\nEstimated Volume: ${form.volume}\nNotes: ${form.notes}`;
    window.open(`https://wa.me/916367315665?text=${encodeURIComponent(message)}`, "_blank");
    setSubmitted(true);
    toast.success("Enquiry sent! We'll get back within 24 hours.");
  };

  return (
    <PageLayout>
      {/* Hero */}
      <section className="pt-28 lg:pt-32 pb-16 bg-gradient-to-br from-sky-900 via-sky-800 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,_rgba(132,204,22,0.15),_transparent_50%)]" />
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 relative">
          <span className="inline-block text-sm font-semibold text-lime-300 tracking-wider uppercase mb-4">
            For Businesses
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5 leading-[1.05] max-w-3xl">
            Corporate & bulk laundry.<br />Handled at scale.
          </h1>
          <p className="text-lg text-sky-100 max-w-2xl leading-relaxed">
            Hotels, hospitals, restaurants, PGs, and offices trust Blitzwash for reliable, SLA-backed laundry at volume-based pricing.
          </p>
          <a href="#enquiry" className="inline-block mt-6 px-6 py-3 rounded-2xl bg-lime-400 hover:bg-lime-300 text-slate-900 font-bold text-sm transition-colors shadow-lg" data-testid="corp-hero-cta">
            Get a Custom Quote
          </a>
        </div>
      </section>

      {/* Who we serve */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-3">Who We Serve</span>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">Trusted by Jaipur's growing businesses</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {CLIENTS.map((c, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-3 hover:border-sky-300 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
                  <c.icon className="w-5 h-5 text-sky-600" />
                </div>
                <p className="text-sm font-semibold text-slate-800">{c.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-20 bg-slate-50/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-3">Why Blitzwash for Business</span>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">Built for volume, priced for scale</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-white rounded-3xl border border-slate-200 p-6">
                <div className="w-11 h-11 rounded-xl bg-lime-100 flex items-center justify-center mb-4">
                  <b.icon className="w-5 h-5 text-lime-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{b.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section id="enquiry" className="py-16 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="bg-slate-900 text-white rounded-3xl p-6 sm:p-10">
            <span className="inline-block text-sm font-semibold text-lime-300 tracking-wider uppercase mb-3">
              Custom Quote
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold mb-2">Tell us about your business</h2>
            <p className="text-sm text-slate-300 mb-8">Fill this in and we'll send a customized quote within 24 hours.</p>

            {submitted ? (
              <div className="bg-lime-500/10 border border-lime-400/30 rounded-2xl p-6 text-center">
                <CheckCircle className="w-10 h-10 text-lime-400 mx-auto mb-3" />
                <p className="text-lg font-bold">Enquiry received!</p>
                <p className="text-sm text-slate-300 mt-2">We'll get back within 24 hours with your custom quote.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <input type="text" required placeholder="Business name *" value={form.business} onChange={(e) => setForm({...form, business: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 placeholder:text-slate-400 focus:border-lime-400 focus:outline-none" data-testid="corp-business-input" />
                <div className="grid sm:grid-cols-2 gap-3">
                  <input type="text" required placeholder="Contact person *" value={form.contact} onChange={(e) => setForm({...form, contact: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 placeholder:text-slate-400 focus:border-lime-400 focus:outline-none" data-testid="corp-contact-input" />
                  <input type="tel" required placeholder="Phone *" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 placeholder:text-slate-400 focus:border-lime-400 focus:outline-none" data-testid="corp-phone-input" />
                </div>
                <input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 placeholder:text-slate-400 focus:border-lime-400 focus:outline-none" data-testid="corp-email-input" />
                <select required value={form.volume} onChange={(e) => setForm({...form, volume: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 focus:border-lime-400 focus:outline-none" data-testid="corp-volume-input">
                  <option value="" className="bg-slate-900">Estimated monthly volume *</option>
                  <option value="Under 100 kg/month" className="bg-slate-900">Under 100 kg / month</option>
                  <option value="100-500 kg/month" className="bg-slate-900">100 – 500 kg / month</option>
                  <option value="500-2000 kg/month" className="bg-slate-900">500 – 2000 kg / month</option>
                  <option value="2000+ kg/month" className="bg-slate-900">2000+ kg / month</option>
                </select>
                <textarea rows={3} placeholder="Any specific requirements? (e.g. daily pickup, specific fabrics, etc.)" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 placeholder:text-slate-400 focus:border-lime-400 focus:outline-none resize-none" data-testid="corp-notes-input" />
                <button type="submit" className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-lime-400 hover:bg-lime-300 text-slate-900 font-bold text-sm transition-colors shadow-lg" data-testid="corp-submit-btn">
                  <Send className="w-4 h-4" /> Send Enquiry
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
